import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { markLanguagePickedByUser } from '@/hooks/useGeoLocale';
import { REGIONS, setRegion, useDisplayRegion, type Region } from '@/lib/region';
import { supabase } from '@/integrations/supabase/client';

interface LanguageSwitcherProps {
  variant?: 'light' | 'dark';
}

/**
 * Country picker (was a two-flag language toggle). One choice drives both
 * the UI language and the pricing currency:
 *   🇺🇸 USA ⇒ English + USD   🇲🇽 México ⇒ Spanish + MXN   🇨🇴 Colombia ⇒ Spanish + COP
 */
const LanguageSwitcher = ({ variant = 'dark' }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const region = useDisplayRegion();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const current = REGIONS.find((r) => r.code === region) ?? REGIONS[0];

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const pick = (code: Region) => {
    const target = REGIONS.find((r) => r.code === code)!;
    markLanguagePickedByUser();
    setRegion(code);
    if (!i18n.language?.startsWith(target.language)) {
      i18n.changeLanguage(target.language);
    }
    setOpen(false);
    // Logged-in users: keep email language in sync with the picked country
    // (best-effort — same persistence the profile dialog does).
    void (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const user = data.session?.user;
        if (!user) return;
        await supabase.from('profiles').update({ preferred_locale: target.language } as any).eq('id', user.id);
        if (user.email) {
          await supabase.rpc('set_recipient_preferred_locale', { _email: user.email, _locale: target.language } as any);
        }
      } catch { /* never block the UI switch */ }
    })();
  };

  const pillCls =
    variant === 'light'
      ? 'bg-neutral-300/40 border-white/15 hover:bg-neutral-300/50 text-white'
      : 'bg-black/8 border-black/8 hover:bg-black/12 text-foreground';
  const menuCls =
    variant === 'light'
      ? 'bg-[#0d1b33]/95 border-white/15 text-white'
      : 'bg-background border-border text-foreground';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 h-9 pl-2.5 pr-2 rounded-full cursor-pointer transition-colors duration-300 backdrop-blur-md border ${pillCls}`}
        aria-label="Select country"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-base select-none leading-none">{current.flag}</span>
        <span className="text-xs font-semibold tracking-wide select-none">{current.label}</span>
        <ChevronDown className={`h-3.5 w-3.5 opacity-70 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className={`absolute right-0 top-full mt-2 z-[60] min-w-[10.5rem] rounded-xl border shadow-lg backdrop-blur-md overflow-hidden ${menuCls}`}
        >
          {REGIONS.map((r) => (
            <button
              key={r.code}
              type="button"
              role="option"
              aria-selected={r.code === region}
              onClick={() => pick(r.code)}
              className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-left transition-colors ${
                r.code === region
                  ? variant === 'light' ? 'bg-white/15 font-semibold' : 'bg-accent font-semibold'
                  : variant === 'light' ? 'hover:bg-white/10' : 'hover:bg-accent/60'
              }`}
            >
              <span className="text-lg leading-none select-none">{r.flag}</span>
              <span className="flex-1 font-semibold tracking-wide">{r.label}</span>
              <span className={`text-[10px] uppercase tracking-wider whitespace-nowrap ${variant === 'light' ? 'text-white/60' : 'text-muted-foreground'}`}>
                {r.language === 'en' ? 'EN · USD' : r.code === 'MX' ? 'ES · MXN' : 'ES · COP'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
